import React from "react";
import clsx from "clsx";
import RuleActions from "../components/RuleActions";
import RenameRulePopup from "./RenameRulePopup";
import RemoveRulePopup from "./RemoveRulePopup";
import { IconAlertTriangle, IconGripVertical } from "@tabler/icons-react";
import { rulesSetStore } from "../RulesSetStore";
import { observer } from "mobx-react-lite";
import { ConditionProps } from "../components/ConditionRow";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

export interface RuleProps {
  id: number;
  name: string;
  conditions: ConditionProps[];
  validationError?: boolean;
}

const RuleRow = (props: {
  index: number;
  rule: RuleProps;
  onClick: () => void;
  moveUp: (index: number) => void;
  moveDown: (index: number) => void;
  moveToTop: (index: number) => void;
  moveToBottom: (index: number) => void;
}) => {
  const [openRenamePopup, setOpenRenamePopup] = React.useState(false);
  const [openRemovePopup, setOpenRemovePopup] = React.useState(false);
  const id = props.rule.id;

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const style = {
    transform: CSS.Translate.toString(transform),
    transition,
    zIndex: isDragging ? 10 : "auto",
  };

  const hasRemove = rulesSetStore.rulesList.length > 1;
  const isFrist = props.index === 0;
  const isLast = props.index === rulesSetStore.rulesList.length - 1;
  const isSelected = props.rule.id === rulesSetStore.currentRule?.id;
  const hasValidationError =
    props.rule.validationError && rulesSetStore.inProgressRuleID !== id;

  return (
    <li
      className={clsx(
        "border-gray-300 border-t px-6 py-[10px] last-of-type:border-b relative",
        "hover:bg-indigo-50",
        "group/ruleRow cursor-pointer",
        {
          "bg-indigo-50 shadow-[inset_0_0_0_1px] !shadow-indigo-600":
            isSelected,
        }
      )}
      style={style}
      onClick={props.onClick}
      ref={setNodeRef}
    >
      <div className="flex items-center">
        <IconGripVertical
          size={20}
          className={clsx(
            "mr-2 min-w-[20px] cursor-grab focus:outline-none text-indigo-700",
            "hidden group-hover/ruleRow:block"
          )}
          {...listeners}
          {...attributes}
        />
        {hasValidationError && (
          <div className="bg-yellow-100 rounded-full p-1.5 w-6 h-6 flex items-center justify-center mr-3">
            <IconAlertTriangle className="text-yellow-700" stroke={3} />
          </div>
        )}
        <span>{`${props.index + 1}. ${props.rule.name}`}</span>
      </div>
      <RuleActions
        onRemoveClicked={hasRemove ? () => setOpenRemovePopup(true) : undefined}
        onEdit={() => setOpenRenamePopup(true)}
        moveUp={isFrist ? undefined : () => props.moveUp(props.index)}
        moveDown={isLast ? undefined : () => props.moveDown(props.index)}
        moveToTop={isFrist ? undefined : () => props.moveToTop(props.index)}
        moveToBottom={
          isLast ? undefined : () => props.moveToBottom(props.index)
        }
      />
      <RenameRulePopup
        rule={props.rule}
        open={openRenamePopup}
        close={() => setOpenRenamePopup(false)}
      />
      <RemoveRulePopup
        ruleID={props.rule.id}
        open={openRemovePopup}
        close={() => setOpenRemovePopup(false)}
      />
    </li>
  );
};

export default observer(RuleRow);
